// The instructor dashboard's model (RFC-043 M3, byj-1wv.4.3): the student
// session cards, the order they are shown in, the filters, and how a frame
// from the class socket changes a card. Pure functions; the page renders.
import type { Assignment, AssignmentRef, ClassAlert, ClassSession, SessionState } from "./api";
import type { Envelope } from "./protocol";
import { t, whenShort } from "./i18n";

export const States: readonly SessionState[] = ["working", "needs-input", "idle", "paused", "ended"];

/** Where a state sorts once alerts are placed: needs-input, working, idle, paused, ended. */
const StateRank: Record<string, number> = { "needs-input": 1, working: 2, idle: 3, paused: 4, ended: 5 };

function stateOf(s: string): SessionState {
  return (States as readonly string[]).includes(s) ? (s as SessionState) : "idle";
}

/** The time of a session's most recent alert, in ms, or 0. */
export function lastAlertAt(s: ClassSession): number {
  let m = 0;
  for (const a of s.alerts) {
    const t = Date.parse(a.at);
    if (t > m) m = t;
  }
  return m;
}

/**
 * The card's group: 0 for a session with alerts that is not over, then by
 * state. An ended session sorts last whatever it raised, since nothing in
 * it can be helped any more.
 */
export function group(s: ClassSession): number {
  if (s.state !== "ended" && s.alerts.length > 0) return 0;
  return StateRank[s.state] ?? 3;
}

/**
 * Alerts first (most recent first), then needs-input, working, idle, paused,
 * ended; within a group the costlier session first, then by student name.
 */
export function compareSessions(a: ClassSession, b: ClassSession): number {
  const g = group(a) - group(b);
  if (g !== 0) return g;
  if (group(a) === 0) {
    const t = lastAlertAt(b) - lastAlertAt(a);
    if (t !== 0) return t;
  }
  const c = b.cost_usd - a.cost_usd;
  if (c !== 0) return c;
  return a.student.name.localeCompare(b.student.name) || a.share.localeCompare(b.share);
}

export function sortSessions(list: readonly ClassSession[]): ClassSession[] {
  return [...list].sort(compareSessions);
}

/** A state filter chip: every session, or one state. */
export type Filter = "all" | SessionState;

/**
 * The alert filter (byj-1wv.51.10): none, a session with any alert that
 * stands ("any"), or with one kind. A kind a session's state already says —
 * a permission's wait, said by needs input — is no alert to filter by.
 */
export type AlertFilter = "" | "any" | string;

/** Alert kinds a session's state says, which the rows and the filters do not say again. */
export const SaidByState: ReadonlySet<string> = new Set(["permission-wait"]);

/** The kinds of a session's alerts that are not said by its state, each once. */
export function signedKinds(s: ClassSession): string[] {
  return [...new Set(s.alerts.map((a) => a.kind).filter((k) => !SaidByState.has(k)))];
}

/** The assignment filter: every session, those on no assignment, or one assignment's key. */
export const NoAssignment = "-";

export function matches(s: ClassSession, f: Filter, q: string, assignment = "", alert: AlertFilter = ""): boolean {
  if (f !== "all" && s.state !== f) return false;
  if (alert !== "" && (alert === "any" ? signedKinds(s).length === 0 : !signedKinds(s).includes(alert))) return false;
  if (assignment === NoAssignment ? !!s.assignment : assignment !== "" && s.assignment?.key !== assignment) return false;
  const needle = q.trim().toLocaleLowerCase();
  return needle === "" || s.student.name.toLocaleLowerCase().includes(needle);
}

/**
 * A card's assignment from a key the feed sent (RFC-043 model 19): the
 * class's assignment of that key, or the key alone when the class list does
 * not have it yet; `late` when the share runs past the due date.
 */
export function assignmentOf(key: string | undefined, assignments: readonly Assignment[], at: string): { assignment?: AssignmentRef; late: boolean } {
  if (!key) return { late: false };
  const a = assignments.find((x) => x.key === key);
  if (!a) return { assignment: { key, title: key }, late: false };
  return { assignment: { key: a.key, title: a.title }, late: !!a.due_at && Date.parse(at) > Date.parse(a.due_at) };
}

/** How many sessions each state chip and the alerts' chip stand for. */
export function counts(list: readonly ClassSession[]): Record<Filter | "alerts", number> {
  const c: Record<Filter | "alerts", number> = { all: list.length, alerts: 0, working: 0, "needs-input": 0, idle: 0, paused: 0, ended: 0 };
  for (const s of list) {
    c[s.state] += 1;
    if (signedKinds(s).length > 0) c.alerts += 1;
  }
  return c;
}

/** The order alert kinds are told in: the ones that ask most of the instructor first. */
export const AlertOrder = ["permission-wait", "turn-failed", "no-progress", "repeated-call", "cost-spike", "idle"];

/** Alert kinds by `AlertOrder`; a kind the client does not know yet comes last. */
export function byPressing(a: string, b: string): number {
  const rank = (k: string) => (AlertOrder.includes(k) ? AlertOrder.indexOf(k) : AlertOrder.length);
  return rank(a) - rank(b);
}

/** How many sessions stand on each kind of alert not said by their state, the most pressing kind first. */
export function kindCounts(list: readonly ClassSession[]): [string, number][] {
  const by = new Map<string, number>();
  for (const s of list) for (const k of signedKinds(s)) by.set(k, (by.get(k) ?? 0) + 1);
  return [...by].sort(([a], [b]) => byPressing(a, b));
}

export interface Totals {
  live: number;
  ended: number;
  cost: number;
}

export function totals(list: readonly ClassSession[]): Totals {
  let live = 0;
  let ended = 0;
  let cost = 0;
  for (const s of list) {
    if (s.state === "ended" || !s.live) ended += 1;
    else live += 1;
    cost += s.cost_usd;
  }
  return { live, ended, cost };
}

/** One chip per alert kind: its latest time and how many times it fired. */
export interface AlertChip {
  kind: string;
  at: string;
  count: number;
  detail?: string;
}

export function alertChips(alerts: readonly ClassAlert[]): AlertChip[] {
  const by = new Map<string, AlertChip>();
  for (const a of alerts) {
    const c = by.get(a.kind);
    if (!c) by.set(a.kind, { kind: a.kind, at: a.at, count: 1, detail: a.detail });
    else {
      c.count += 1;
      if (Date.parse(a.at) >= Date.parse(c.at)) {
        c.at = a.at;
        c.detail = a.detail;
      }
    }
  }
  return [...by.values()].sort((x, y) => Date.parse(y.at) - Date.parse(x.at));
}

/**
 * byj-share's name for a share nobody named — "byj <session id>", "Claude
 * Code <uuid>", "terminal <tty>" — the relay's `autoTitle` (recordings.go),
 * its tool, a byj id's epoch and a uuid's first part caught.
 */
const AutoTitle =
  /^(?:(byj|codex|Claude Code) (?:(\d{10,})-[0-9a-f]{6,}|([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})|(terminal) (\S+))$/;

/** A share's name as a person reads it, and whether someone gave it one. */
export interface SessionLabel {
  text: string;
  named: boolean;
}

/**
 * A share's title as the class pages show it (byj-1wv.51.10): one its owner
 * gave (--title) as it is; byj-share's default, which says nothing a person
 * can tell apart, as its tool and when it started — the share's own start
 * when known, else a byj id's, which begins with its epoch — or, with
 * neither, the id's first part.
 */
export function sessionLabel(title: string, id: string, startedAt?: string, now: Date = new Date()): SessionLabel {
  if (!title) return { text: id, named: false };
  const m = AutoTitle.exec(title);
  if (!m) return { text: title, named: true };
  const tool = m[1] ?? m[4] ?? "";
  const epoch = m[2] ? Number(m[2]) * (m[2].length >= 13 ? 1 : 1000) : NaN;
  const ms = startedAt ? Date.parse(startedAt) : epoch;
  // a time a session could have started at; an id of µs or ns says none
  if (ms >= Date.UTC(2000, 0, 1) && ms <= now.getTime() + 86_400_000) return { text: t("classes:sessionStarted", { tool, at: whenShort(new Date(ms).toISOString(), now) }), named: false };
  const short = m[3] ?? m[5];
  return { text: short ? t("classes:sessionShort", { tool, id: short }) : title, named: false };
}

/**
 * How far the instructor has marked a session's alerts seen: the last event
 * an alert of it pointed at, and for one that points at none (going quiet)
 * when it was raised. By its events first, since an alert's time is the
 * clock of whoever stamped it — the student's machine on the feed, the relay
 * in the list — and a mark of one clock read against the other would bring
 * seen alerts back after a reload; a share's seqs are the same on both.
 */
export interface SeenMark {
  seq?: number;
  at?: string;
}

/** The mark that sees every alert a session has now, but those its state says. */
export function seenMark(s: ClassSession): SeenMark | undefined {
  const marked = s.alerts.filter((a) => !SaidByState.has(a.kind));
  if (marked.length === 0) return undefined;
  const seqs = marked.flatMap((a) => a.seqs ?? []);
  const times = marked.filter((a) => !a.seqs?.length).map((a) => Date.parse(a.at));
  return { ...(seqs.length ? { seq: Math.max(...seqs) } : {}), ...(times.length ? { at: new Date(Math.max(...times)).toISOString() } : {}) };
}

function seenBy(a: ClassAlert, mark: SeenMark): boolean {
  if (a.seqs?.length) return mark.seq !== undefined && Math.max(...a.seqs) <= mark.seq;
  return mark.at !== undefined && Date.parse(a.at) <= Date.parse(mark.at);
}

/**
 * The alerts a session raised that still stand, on the instructor's side
 * (byj-1wv.51.10). An alert is sent once and never taken back — the relay
 * keeps each on the card until the share ends — so the dashboard tells over
 * from current by what the card itself says: a permission wait stands while
 * a permission is still pending, whatever was marked seen, and going quiet
 * until the session does something again. Any other the instructor marked
 * seen (`seenMark`) stands no more, and a new one brings the session back.
 * The rest — a repeated call, a turn that failed or made no progress, a cost
 * spike — are of a turn, and nothing on the card says the turn is behind
 * it: they stand until seen (RFC-043 "Known gaps").
 */
export function currentAlerts(s: ClassSession, seen?: SeenMark): ClassAlert[] {
  const last = s.last_event_at ? Date.parse(s.last_event_at) : NaN;
  return s.alerts.filter((a) => {
    if (a.kind === "permission-wait") return s.pending_permissions > 0;
    if (seen && seenBy(a, seen)) return false;
    if (a.kind === "idle") return !(last > Date.parse(a.at));
    return true;
  });
}

/**
 * The state the class pages say: a session waiting on a permission needs
 * input whatever its status last said. The sidecar makes a session
 * needs-input as a permission is asked and working again once none is
 * pending, but a turn that begins, or ends, while one still is sets
 * working, or idle, over it (sidecar `track`); paused and ended stand.
 */
export function shownState(s: ClassSession): SessionState {
  return s.pending_permissions > 0 && (s.state === "working" || s.state === "idle") ? "needs-input" : s.state;
}

/**
 * The session as the dashboard shows it (byj-1wv.51.10): in the state it
 * says (`shownState`) and with only its alerts that still stand
 * (`currentAlerts`) — itself, the same object, when nothing changes, so a
 * memoized row does not draw again. Its order, its group, the chips' counts
 * and the filters all go by this.
 */
export function asShown(s: ClassSession, seen?: SeenMark): ClassSession {
  const alerts = currentAlerts(s, seen);
  const state = shownState(s);
  return alerts.length === s.alerts.length && state === s.state ? s : { ...s, alerts, state };
}


function sameAlert(a: ClassAlert, b: ClassAlert): boolean {
  return a.kind === b.kind && a.at === b.at && (a.detail ?? "") === (b.detail ?? "");
}

/**
 * Apply one class-socket frame to the cards. A card that changes is a new
 * object and every other card keeps its identity, so a memoized card
 * re-renders only when its own session moved. A frame about a share with
 * no card leaves the list as it was; `unknownShare` says to reload.
 */
export function applyClassFrame(list: ClassSession[], e: Envelope, assignments: readonly Assignment[] = []): ClassSession[] {
  const id = e.share;
  if (!id) return list;
  const i = list.findIndex((s) => s.share === id);
  if (i < 0) return list;
  const s = list[i]!;
  let next: ClassSession | null = null;
  switch (e.type) {
    case "status": {
      const state = stateOf(e.body.state);
      const live = state !== "ended";
      // the sidecar says its status every 5 s whether or not anything moved:
      // only one that moves something is the session doing something, as the
      // relay's card counts events alone (byj-1wv.51.10)
      const moved =
        state !== s.state || live !== s.live || e.body.turns !== s.turns || e.body.cost_usd !== s.cost_usd || e.body.pending_permissions !== s.pending_permissions;
      // none pending, the waits are over: a permission asked next is not the one that waited
      const alerts =
        e.body.pending_permissions === 0 && s.alerts.some((a) => a.kind === "permission-wait") ? s.alerts.filter((a) => a.kind !== "permission-wait") : s.alerts;
      if (!moved && alerts === s.alerts) return list;
      next = {
        ...s,
        state,
        live,
        turns: e.body.turns,
        cost_usd: e.body.cost_usd,
        pending_permissions: e.body.pending_permissions,
        alerts,
        ...(moved ? { last_event_at: e.ts } : {}),
      };
      break;
    }
    case "alert": {
      const a: ClassAlert = { kind: e.body.kind, at: e.ts, detail: e.body.detail, seqs: e.body.seqs };
      if (s.alerts.some((x) => sameAlert(x, a))) return list;
      next = { ...s, alerts: [...s.alerts, a] };
      break;
    }
    case "share.open": {
      // the relay's own share.open says a share's assignment changed and
      // nothing of its state (RFC-043 model 19)
      const { assignment, late } = assignmentOf(e.body.target.assignment, assignments, s.last_event_at || e.ts);
      if (e.from.kind === "relay") {
        next = { ...s, assignment, late };
        break;
      }
      const paused = e.body.policy.paused;
      const state: SessionState = paused ? "paused" : s.state === "paused" || s.state === "ended" ? "idle" : s.state;
      // a sidecar back without naming one keeps what the share had
      const kept = e.body.target.assignment ? { assignment, late } : { assignment: s.assignment, late: s.late };
      next = { ...s, title: e.body.title || s.title, source: e.body.source, live: true, state, last_event_at: e.ts, ...kept };
      break;
    }
    case "share.close":
      next = { ...s, live: false, state: "ended", pending_permissions: 0, last_event_at: e.ts };
      break;
    default:
      return list;
  }
  const out = list.slice();
  out[i] = next;
  return out;
}

/** Whether a frame names a share the dashboard has no card for (a student just started one). */
export function unknownShare(list: readonly ClassSession[], e: Envelope): boolean {
  return (
    !!e.share &&
    (e.type === "share.open" || e.type === "status" || e.type === "alert") &&
    !list.some((s) => s.share === e.share)
  );
}
